import i18nFactory from '@yandex-int/i18n';
import { compact } from 'lodash';
import * as React from 'react';

import { BannerPreview } from '../../components/BannerPreview/BannerPreview';
import { GridBannerStatusCell } from '../../components/GridBannerStatusCell/GridBannerStatusCell';
import { GridBannerTypeCell } from '../../components/GridBannerTypeCell/GridBannerTypeCell';
import { GridCampaignTypeCell } from '../../components/GridCampaignTypeCell/GridCampaignTypeCell';
import { GridESSStatusCellContainer } from '../../components/GridESSStatusCell/GridESSStatusCell';
import { GridESSStatusCellJSONContainer } from '../../components/GridESSStatusCellJSON/GridESSStatusCellJSON';
import { GridParentCell } from '../../components/GridParentCell/GridParentCell';
import { EditableBannerPriceCellContainer } from '../../containers/EditableBannerPriceCellContainer/EditableBannerPriceCellContainer';
import { GridActionsMenuContainer } from '../../containers/GridActionsMenuContainer/GridActionsMenuContainer';
import { addSettingsToDecl, arrangeInOrder } from '../../service/DeclarationHelper';
import { isStatusTextVisible } from '../../service/GridHelpers/GridHelpers';
import { banners, ColumnsSettings, ColumnsSettingsViewDict } from '../../typings/common';
import { ConstData } from '../../typings/constants';
import { DocCenterColumnTooltip } from '../../typings/doc-center';
import { Banner } from '../../typings/domain';
import { CurrencyCode } from '../../typings/graphql-grid';
import {
    GridBannerItemActions,
    GridCellProps,
    GridColumnDecl,
    GridColumnsOptions,
    GridScreenTotalKey,
    OptionalGridColumnDecl,
    SortDirection
} from '../../typings/grid';
import { ClientFeatures } from '../../typings/state';
import { getDefaultGoalsViewInfo, getGoalsColumnsDecl } from '../goals/goals';
import { getDynamicStatsColumnsFields, getStaticStatsColumns } from '../stats/stats';

import * as keyset from './banner.i18n';

const i18n = i18nFactory(keyset);

type BannersGridColumnsOptions = GridColumnsOptions & {
    isSelectedSeveralGroups: boolean;
    isBannerPricesOnGridEnabled: boolean;
};

export enum GridBannersColumnKey {
    bannerType = 'banner_type',
    campaignType = 'campaign-type',
    settings = 'settings',
    id = 'id',
    status = 'status',
    campaignId = 'campaign_id',
    price = 'price',
    aggregatedStatus = 'aggregated-status',
    aggregatedStatusInfo = 'aggregated-status-info'
}

const getFixedBannerColumns = (
    features: ClientFeatures,
    canvasDomain?: string,
    clientId?: string
): Array<OptionalGridColumnDecl<Banner, GridBannerItemActions, BannersGridColumnsOptions>> => {
    return [
        {
            title: '',
            key: GridBannersColumnKey.settings,
            cellClass: 'settings-cell',
            isSortable: false,
            mandatory: true,
            fixed: true,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 35,
            CellComponent: React.memo(
                ({ rowIndex, actions }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => (
                    <GridActionsMenuContainer index={rowIndex} actions={actions} />
                )
            )
        },
        {
            title: i18n('type'),
            key: GridBannersColumnKey.bannerType,
            cellClass: 'banner-type-cell',
            defaultSortDirection: SortDirection.asc,
            fixed: true,
            minWidth: 35,
            maxWidth: 65,
            align: 'right',
            tooltipKey: DocCenterColumnTooltip.campaignType,
            CellComponent: React.memo(
                ({
                    rowData: {
                        bannerType,
                        group: {
                            campaign: { type: campaignType }
                        }
                    }
                }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    return <GridBannerTypeCell type={bannerType} campaignType={campaignType} features={features} />;
                }
            )
        },
        {
            title: i18n('banner'),
            key: GridBannersColumnKey.id,
            fixed: true,
            mandatory: true,
            totalKey: GridScreenTotalKey.name,
            isAlignedToLeft: true,
            minWidth: 445,
            maxWidth: 500,
            CellComponent: React.memo(
                ({ rowData: banner, actions }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    const { recommendations } = banner;

                    let recommendationsParams;
                    let showBannerCreativePreview;

                    if (recommendations.data) {
                        recommendationsParams = {
                            items: recommendations,
                            onOpenRecommendationsPopup: actions.onOpenRecommendationsPopup,
                            onShowRecommendation: actions.onShowRecommendation,
                            onSendLicenseButtonClick: actions.onSendLicenseButtonClick
                        };
                    }
                    if (banner.livePreviewUrl) {
                        showBannerCreativePreview = actions.showBannerCreativePreview;
                    }

                    return (
                        <BannerPreview
                            bannerPreviewData={banner}
                            canvasDomain={canvasDomain}
                            additionsFlags={banner.additionsFlags}
                            clientId={clientId}
                            recommendations={recommendationsParams}
                            showBannersPreview={actions.showBannersPreview}
                            showBannerCreativePreview={showBannerCreativePreview}
                        />
                    );
                }
            )
        }
    ];
};

const getBannerColumnsBase = (
    options: BannersGridColumnsOptions,
    columnsSettings: ColumnsSettings,
    isCompactViewEnabled: boolean,
    isAggregatedStatusAllowed: boolean
): Array<OptionalGridColumnDecl<Banner, GridBannerItemActions, BannersGridColumnsOptions>> => {
    return compact([
        {
            title: i18n('price'),
            key: GridBannersColumnKey.price,
            isAlignedToLeft: true,
            isSortable: false,
            minWidth: 135,
            maxWidth: 135,
            isAvailable: ({ isBannerPricesOnGridEnabled }: BannersGridColumnsOptions): boolean => {
                return isBannerPricesOnGridEnabled;
            },
            CellComponent: React.memo(
                ({ rowData: banner, rowIndex }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    const { id, bannerType } = banner;

                    return <EditableBannerPriceCellContainer index={rowIndex} bannerType={bannerType} bannerId={id} />;
                }
            )
        },
        {
            title: i18n('status'),
            key: GridBannersColumnKey.status,
            isSortable: false,
            isAlignedToLeft: true,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: { status, rejectReasons, id, bannerType, operators },
                    actions
                }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    const columnViewInfo = columnsSettings.columnViewInfo[GridBannersColumnKey.status];

                    return (
                        <GridBannerStatusCell
                            bid={id}
                            bannerType={bannerType}
                            status={status}
                            rejectReasons={rejectReasons}
                            lang={options.currentLang}
                            tld={options.tld}
                            clientRegion={options.clientRegion}
                            isOperatorAgency={options.operator.isAgency}
                            isOperatorSelfAgency={options.operator.isSelfAgency}
                            getModerationRejectReasons={actions.getModerationRejectReasons}
                            isTextVisible={isStatusTextVisible(isCompactViewEnabled, columnViewInfo)}
                            operators={operators}
                        />
                    );
                }
            )
        },
        {
            title: i18n('campaign-type'),
            key: GridBannersColumnKey.campaignType,
            defaultSortDirection: SortDirection.asc,
            isSortable: false,
            minWidth: 35,
            maxWidth: 105,
            align: 'right',
            CellComponent: React.memo(
                ({
                    rowData: {
                        group: {
                            campaign: { type: campaignType }
                        }
                    }
                }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    return <GridCampaignTypeCell type={campaignType} />;
                }
            )
        },
        {
            title: i18n('campaign-group'),
            key: GridBannersColumnKey.campaignId,
            isAlignedToLeft: true,
            minWidth: 300,
            maxWidth: 600,
            CellComponent: React.memo(
                ({ rowData: { group } }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => (
                    <GridParentCell campaign={group.campaign} group={group} />
                )
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status',
            key: GridBannersColumnKey.aggregatedStatus,
            isSortable: false,
            minWidth: 35,
            maxWidth: 1000,
            CellComponent: React.memo(
                ({ rowData: { aggregatedStatus } }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element => {
                    return <GridESSStatusCellJSONContainer aggregatedStatus={aggregatedStatus} />;
                }
            )
        },
        isAggregatedStatusAllowed && {
            title: 'New status2',
            key: GridBannersColumnKey.aggregatedStatusInfo,
            isSortable: false,
            minWidth: 35,
            maxWidth: 300,
            CellComponent: React.memo(
                ({
                    rowData: { aggregatedStatusInfo }
                }: GridCellProps<Banner, GridBannerItemActions>): JSX.Element | null => {
                    if (!aggregatedStatusInfo) {
                        return null;
                    }

                    return (
                        <GridESSStatusCellContainer
                            status={aggregatedStatusInfo.status}
                            reasons={aggregatedStatusInfo.reasons}
                            counters={aggregatedStatusInfo.counters}
                        />
                    );
                }
            )
        }
    ]);
};

export const bannersColumnOrder = [
    'settings',
    'banner_type',
    'id',
    'status',
    'campaign-type',
    'campaign_id',
    'aggregated-status',
    'aggregated-status-info',
    'price',
    'GOALS',
    'STAT_COST',
    'STAT_GOALS',
    'STAT_REVENUE',
    'STAT_PROFITABILITY',
    'STAT_AVG_GOAL_COST',
    'STAT_AVG_CLICK_COST',
    'STAT_SHOWS',
    'STAT_CPM_PRICE',
    'STAT_CLICKS',
    'STAT_CTR',
    'STAT_CONVERSION_RATE',
    'STAT_BOUNCE_RATE'
];

export function getDefaultDynamicBannerColumnsFields(): ColumnsSettings {
    return {
        columnOrder: bannersColumnOrder,
        columnViewInfo: {
            settings: {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            banner_type: {
                width: 50,
                widthCompact: 50,
                hidden: false
            },
            id: {
                width: 445,
                widthCompact: 445,
                hidden: false
            },
            price: {
                width: 135,
                widthCompact: 135,
                hidden: false
            },
            status: {
                width: 200,
                widthCompact: 200,
                hidden: false
            },
            'aggregated-status': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            'aggregated-status-info': {
                width: 250,
                widthCompact: 250,
                hidden: false
            },
            'campaign-type': {
                width: 35,
                widthCompact: 35,
                hidden: false
            },
            campaign_id: {
                width: 300,
                widthCompact: 300,
                hidden: false
            },
            ...getDefaultGoalsViewInfo(),
            ...getDynamicStatsColumnsFields()
        }
    };
}

export function getBannerColumns(
    options: BannersGridColumnsOptions,
    columnsSettings: ColumnsSettingsViewDict,
    clientCurrency: CurrencyCode,
    isCompactViewEnabled: boolean,
    isGoalsStatEnabled: boolean,
    isAggregatedStatusAllowed: boolean,
    hasSelectedGoals: boolean,
    features: ClientFeatures,
    hasSelectedGoalsAtColumnsSettingsEditor?: boolean,
    isColumnsSettingsEditorLocation?: boolean,
    constants?: ConstData,
    clientId?: string
): Array<GridColumnDecl<Banner>> {
    const canvasDomain = constants ? constants.canvasDomain : undefined;
    const fixedColumns = getFixedBannerColumns(features, canvasDomain, clientId).map(column => {
        return addSettingsToDecl(column, columnsSettings, banners);
    });

    const result = getBannerColumnsBase(
        options,
        columnsSettings[banners],
        isCompactViewEnabled,
        isAggregatedStatusAllowed
    )
        .concat(getGoalsColumnsDecl<Banner>(hasSelectedGoalsAtColumnsSettingsEditor, isColumnsSettingsEditorLocation))
        .concat(
            getStaticStatsColumns<Banner>(
                {
                    getCurrency: (item: Banner): CurrencyCode => item.group.campaign.currency
                },
                clientCurrency,
                isCompactViewEnabled,
                hasSelectedGoals,
                isGoalsStatEnabled,
                hasSelectedGoalsAtColumnsSettingsEditor,
                isColumnsSettingsEditorLocation
            )
        )
        .filter(column => {
            return column.isAvailable ? column.isAvailable(options) : true;
        })
        .map(column => {
            return addSettingsToDecl(column, columnsSettings, banners);
        });

    return fixedColumns.concat(arrangeInOrder(result, columnsSettings[banners].columnOrder, true));
}
